/**
 * COMO SE VE EL FONDO EN EL SALON DEL DESKTOP (FONDO-PANEL-T1 punto 2, Ares #25944).
 *
 * PARA QUE. Antes de aceptar una propuesta de fondo, el panel muestra donde va a quedar: que se
 * recorta y que tapan las tarjetas del salon. Asi se sabe si el logo y el sello quedan a la vista.
 *
 * DE DONDE SALE, y por que NO es el marco que describia la tarea. La tarea pedia barra lateral
 * 260, cabecera, columna 320 y recorte 3:2 sobre la imagen entera. Medido en Nexo Desktop main
 * 87f8147 y en la CAPTURA REAL de D (#25940, #25949: CAPTURA-FONDO-DESKTOP_2026-10-12.png, sha256
 * 19c9f03e21b9327c, 1366x705 = 1366x768 maximizada, DPI 96):
 *   - el fondo vive SOLO en el area del salon: x 207..1366, y 56..677 = 1159 x 621 (relacion 1,87).
 *     La barra lateral (207) y la cabecera (56) quedan FUERA: no tapan el fondo, no lo tocan.
 *   - lo pinta CUBRIENDO y centrado (FrmPlanoSalonV15.HornearLienzo, escala Math.Max): una imagen
 *     16:9 pierde un poco arriba y abajo; una mas ancha pierde los lados.
 *   - las bandas del titulo, la leyenda y la columna derecha son TRANSPARENTES con fondo
 *     (AplicarFondoSalon). Lo que tapa son las piezas SOLIDAS de abajo, medidas en la captura en
 *     pixeles del salon (origen x 207, y 56).
 *
 * Las mesas dependen de cuantas tenga el negocio: el modelo usa las 20 de la captura (2 filas) y
 * lo dice. Sin DOM: lo prueba scripts/check-marco-salon.mjs.
 */

export const SALON = { ancho: 1159, alto: 621 };
export const ORIGEN = 'captura real del salon de D (#25940/#25949), 1366x705 al 100 %';

/** Las piezas solidas, en pixeles del salon de 1159x621. */
const PIEZAS = {
  restaurante: [
    { clave: 'titulo', titulo: 'Tarjeta "Pedidos · Plano de Salon" y leyenda', x: 4, y: 7, ancho: 280, alto: 65 },
    { clave: 'caja', titulo: 'Caja abierta', x: 1029, y: 19, ancho: 120, alto: 22 },
    { clave: 'botones', titulo: 'Botones Salon / Domicilios / Llevar', x: 911, y: 48, ancho: 228, alto: 34 },
    { clave: 'mesas', titulo: 'Mesas (2 filas con 20 mesas)', x: 28, y: 99, ancho: 861, alto: 127 },
    { clave: 'columna', titulo: 'Tarjetas Domicilios y Resumen del turno', x: 931, y: 85, ancho: 205, alto: 270 }
  ],
  mostrador: [
    { clave: 'titulo', titulo: 'Tarjeta "Mostrador"', x: 4, y: 7, ancho: 97, alto: 31 },
    { clave: 'caja', titulo: 'Caja abierta', x: 1029, y: 19, ancho: 120, alto: 22 },
    { clave: 'puestos', titulo: 'CAJA y puestos en espera', x: 28, y: 57, ancho: 228, alto: 56 }
  ]
};

export const MODOS = Object.keys(PIEZAS);

/** Las piezas que tapan, en fracciones (0 a 1) del salon, para dibujarlas con porcentajes. */
export function piezasQueTapan(modo = 'restaurante') {
  const lista = PIEZAS[modo] || PIEZAS.restaurante;
  return lista.map((p) => ({
    clave: p.clave,
    titulo: p.titulo,
    x: p.x / SALON.ancho,
    y: p.y / SALON.alto,
    ancho: p.ancho / SALON.ancho,
    alto: p.alto / SALON.alto
  }));
}

/**
 * Que parte de la imagen se ve en el salon (cubrir y centrar), en fracciones de la imagen.
 * @returns {{ x, y, ancho, alto, recorta: 'nada'|'los lados'|'arriba y abajo', perdidaPct: number }}
 */
export function recorteDelSalon(anchoImagen, altoImagen) {
  const wi = Number(anchoImagen) > 0 ? Number(anchoImagen) : 0;
  const hi = Number(altoImagen) > 0 ? Number(altoImagen) : 0;
  if (!wi || !hi) return { x: 0, y: 0, ancho: 1, alto: 1, recorta: 'nada', perdidaPct: 0 };
  const propImagen = wi / hi;
  const propSalon = SALON.ancho / SALON.alto;
  if (Math.abs(propImagen - propSalon) < 0.005) return { x: 0, y: 0, ancho: 1, alto: 1, recorta: 'nada', perdidaPct: 0 };
  if (propImagen > propSalon) {
    const visible = propSalon / propImagen;
    return { x: (1 - visible) / 2, y: 0, ancho: visible, alto: 1, recorta: 'los lados', perdidaPct: Math.round((1 - visible) * 100) };
  }
  const visible = propImagen / propSalon;
  return { x: 0, y: (1 - visible) / 2, ancho: 1, alto: visible, recorta: 'arriba y abajo', perdidaPct: Math.round((1 - visible) * 100) };
}

/** Que fraccion del salon queda a la vista (sin piezas encima). Las piezas no se solapan entre si. */
export function fraccionLibre(modo = 'restaurante') {
  const tapada = piezasQueTapan(modo).reduce((s, p) => s + p.ancho * p.alto, 0);
  return Math.max(0, 1 - tapada);
}

/** Una linea para el dueno, sin numeros crudos de pixeles. */
export function resumenDelSalon(anchoImagen, altoImagen, modo = 'restaurante') {
  const r = recorteDelSalon(anchoImagen, altoImagen);
  const recorte = r.recorta === 'nada'
    ? 'En el salon la imagen se ve entera'
    : `En el salon se pierde ${r.perdidaPct < 1 ? 'menos del 1' : r.perdidaPct}% de la imagen (${r.recorta})`;
  const libre = Math.round(fraccionLibre(modo) * 100);
  return `${recorte}; las tarjetas tapan la parte de arriba y queda a la vista el ${libre}% del salon. `
    + 'El logo y el sello tienen que caer en el centro o abajo.';
}
